import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, holidayApi } from '../api';
import { Spinner, Avatar, RoleBadge, StatusBadge, PriorityBadge } from '../components/ui';

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function timeGreet() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}
function fmt(n) { return n ?? 0; }

function LoadingPage() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <Spinner size={36} />
        <p style={{ marginTop: '16px', color: '#6B7280', fontSize: '14px' }}>Loading dashboard…</p>
      </div>
    </div>
  );
}

/* ── Welcome Banner ─────────────────────────────────────────────────────── */
function WelcomeBanner({ user, stats }) {
  const navigate = useNavigate();
  return (
    <div style={{
      borderRadius: '20px', overflow: 'hidden',
      background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 60%, #6366F1 100%)',
      marginBottom: '24px',
      position: 'relative',
    }}>
      {/* Decorative circles */}
      <div style={{ position: 'absolute', top: '-30px', right: '-20px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
      <div style={{ position: 'absolute', bottom: '-40px', right: '60px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

      <div style={{ padding: '24px', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px', fontWeight: 500 }}>{timeGreet()}</p>
            <h2 style={{
              fontSize: '22px', fontWeight: 800, color: '#fff',
              letterSpacing: '-0.04em', fontFamily: "'Sora', sans-serif",
              marginBottom: '6px', lineHeight: 1.2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{user?.name}</h2>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.15)', borderRadius: '999px', padding: '4px 10px 4px 6px' }}>
              <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {user?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>
          <Avatar name={user?.name || '?'} size={48} style={{ border: '2px solid rgba(255,255,255,0.3)', flexShrink: 0 }} />
        </div>

        {/* Quick stats strip */}
        {stats && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '20px', flexWrap: 'wrap' }}>
            {[
              { label: 'Total', val: fmt(stats.total),    color: 'rgba(255,255,255,0.9)' },
              { label: 'Active', val: fmt(stats.active),  color: '#60A5FA' },
              { label: 'Overdue', val: fmt(stats.overdue), color: '#FCA5A5' },
              { label: 'Done', val: fmt(stats.done),       color: '#6EE7B7' },
            ].map((s, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.1)', borderRadius: '12px',
                padding: '8px 14px', textAlign: 'center', flex: '1 1 0', minWidth: '60px',
              }}>
                <p style={{ fontSize: '20px', fontWeight: 800, color: s.color, letterSpacing: '-0.03em', lineHeight: 1 }}>{s.val}</p>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginTop: '3px', fontWeight: 500 }}>{s.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Stat card grid ─────────────────────────────────────────────────────── */
function StatGrid({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '24px' }}>
      {items.map((item, i) => (
        <StatTile key={i} {...item} />
      ))}
    </div>
  );
}

function StatTile({ label, value, icon, color, bg, onClick, badge }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: bg || '#fff',
        borderRadius: '16px',
        border: '1px solid #E5E7EB',
        padding: '16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s, box-shadow 0.15s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        position: 'relative', overflow: 'hidden',
      }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)'; } }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <p style={{ fontSize: '12px', color: '#6B7280', fontWeight: 500, letterSpacing: '0.01em' }}>{label}</p>
        <span style={{ fontSize: '18px' }}>{icon}</span>
      </div>
      <p style={{ fontSize: '28px', fontWeight: 800, color: color || '#111827', letterSpacing: '-0.04em', lineHeight: 1 }}>{fmt(value)}</p>
      {badge && (
        <span style={{
          display: 'inline-block', marginTop: '6px',
          padding: '2px 8px', borderRadius: '999px',
          background: `${color}18`, color, fontSize: '11px', fontWeight: 600,
        }}>{badge}</span>
      )}
      {/* Decorative dot */}
      <div style={{ position: 'absolute', bottom: '-12px', right: '-12px', width: '48px', height: '48px', borderRadius: '50%', background: `${color}12` }} />
    </div>
  );
}

/* ── Section header ─────────────────────────────────────────────────────── */
function SectionHead({ title, count, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#111827', letterSpacing: '-0.02em' }}>{title}</h3>
        {count !== undefined && (
          <span style={{ background: '#F1F5F9', color: '#6B7280', borderRadius: '999px', fontSize: '11px', fontWeight: 700, padding: '2px 8px' }}>{count}</span>
        )}
      </div>
      {action}
    </div>
  );
}

/* ── Mini task row ──────────────────────────────────────────────────────── */
function TaskRow({ task }) {
  const navigate  = useNavigate();
  const isOverdue = task.status === 'OVERDUE';
  const isDueToday = task.deadline === new Date().toISOString().split('T')[0];
  return (
    <div
      onClick={() => navigate(`/tasks/${task.id}`)}
      style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '12px 14px', borderRadius: '12px', cursor: 'pointer',
        background: '#fff',
        border: `1px solid ${isOverdue ? '#FECACA' : '#F1F5F9'}`,
        transition: 'background 0.1s, box-shadow 0.1s',
        marginBottom: '6px',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFF'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Priority bar */}
      <div style={{
        width: '3px', height: '36px', borderRadius: '2px', flexShrink: 0,
        background: isOverdue ? '#EF4444' : isDueToday ? '#F59E0B' :
                    task.priority === 'CRITICAL' ? '#EF4444' :
                    task.priority === 'HIGH' ? '#F97316' :
                    task.priority === 'MEDIUM' ? '#3B82F6' : '#94A3B8',
      }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontWeight: 600, fontSize: '14px', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
          {task.title}
        </p>
        <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>
          {task.department?.name || '—'}
        </p>
      </div>

      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {isOverdue ? (
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#EF4444' }}>{task.daysOverdue}d late</span>
        ) : (
          <span style={{ fontSize: '12px', color: isDueToday ? '#B45309' : '#9CA3AF', fontWeight: isDueToday ? 600 : 400 }}>
            {isDueToday ? 'Today' : task.deadline}
          </span>
        )}
      </div>
    </div>
  );
}

function TaskBlock({ title, tasks = [], emptyText, onViewAll }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? tasks : tasks.slice(0, 4);
  return (
    <div style={{ marginBottom: '24px' }}>
      <SectionHead
        title={title}
        count={tasks.length}
        action={tasks.length > 4 && (
          <button onClick={() => setExpanded(e => !e)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#2563EB', fontWeight: 600 }}>
            {expanded ? 'Show less' : `See all ${tasks.length}`}
          </button>
        )}
      />
      {tasks.length === 0
        ? <p style={{ fontSize: '13px', color: '#9CA3AF', padding: '12px 0' }}>{emptyText}</p>
        : shown.map(t => <TaskRow key={t.id} task={t} />)
      }
    </div>
  );
}

/* ── Super Admin Dashboard ─────────────────────────────────────────────── */

/* ── Who's Out Today widget ───────────────────────────────────────────────── */
function WhosOutWidget() {
  const [onLeave, setOnLeave] = useState([]);
  const [loaded,  setLoaded]  = useState(false);

  useEffect(() => {
    holidayApi.getOnLeaveToday()
      .then(r => setOnLeave(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || onLeave.length === 0) return null;

  return (
    <div style={{
      background: '#FFFBEB', border: '1px solid #FDE68A',
      borderRadius: '14px', padding: '14px 16px', marginBottom: '20px',
    }}>
      <p style={{ fontWeight: 700, fontSize: '14px', color: '#B45309', marginBottom: '10px' }}>
        🏖 On Leave Today — {onLeave.length} {onLeave.length === 1 ? 'employee' : 'employees'}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {onLeave.map(e => (
          <div key={e.employeeId} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            background: '#FEF3C7', borderRadius: '999px', padding: '4px 10px 4px 4px',
          }}>
            <div style={{
              width: '22px', height: '22px', borderRadius: '50%',
              background: '#F59E0B', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '10px', fontWeight: 800,
            }}>
              {e.name?.[0]?.toUpperCase() || '?'}
            </div>
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#92400E' }}>{e.name}</span>
            <span style={{ fontSize: '11px', color: '#B45309' }}>· {e.holidayName}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SuperAdminDashboard() {
  const { user }           = useAuth();
  const navigate           = useNavigate();
  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(true);

  useEffect(() => {
    dashboardApi.superAdmin().then(r => setData(r.data.data)).catch(() => {}).finally(() => setLoad(false));
  }, []);

  if (loading) return <LoadingPage />;
  const s = data || {};

  const bannerStats = {
    total:   (s.totalTasks || 0),
    active:  (s.inProgressTasks || 0),
    overdue: (s.overdueTasks || 0),
    done:    (s.doneTasks || 0),
  };

  return (
    <div style={P.wrap}>
      <WelcomeBanner user={user} stats={bannerStats} />

      <WhosOutWidget />

      <StatGrid items={[
        { label: 'Total Tasks',   value: s.totalTasks,    icon: '📋', color: '#2563EB', bg: '#EFF6FF', onClick: () => navigate('/tasks') },
        { label: 'Overdue',       value: s.overdueTasks,  icon: '🚨', color: '#EF4444', bg: '#FEF2F2', onClick: () => navigate('/tasks/overdue'), badge: s.overdueTasks > 0 ? 'Needs attention' : 'All clear' },
        { label: 'In Progress',   value: s.inProgressTasks, icon: '⚡', color: '#F59E0B', bg: '#FFFBEB' },
        { label: 'Completed',     value: s.doneTasks,     icon: '✅', color: '#10B981', bg: '#ECFDF5' },
      ]} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '24px' }}>
        {[
          { label: 'Employees',   value: s.totalEmployees,  icon: '👥', color: '#6366F1' },
          { label: 'Departments', value: s.totalDepartments,icon: '🏢', color: '#0EA5E9' },
        ].map((item, i) => (
          <StatTile key={i} {...item} />
        ))}
      </div>

      <TaskBlock title="🚨 Overdue Tasks"   tasks={s.recentOverdueTasks || []} emptyText="No overdue tasks — great work!" />
      <TaskBlock title="⚡ Active Tasks"    tasks={s.recentInProgressTasks || []} emptyText="No tasks in progress" />
    </div>
  );
}

/* ── Admin Dashboard ──────────────────────────────────────────────────── */
export function AdminDashboard() {
  const { user }           = useAuth();
  const navigate           = useNavigate();
  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(true);

  useEffect(() => {
    dashboardApi.admin().then(r => setData(r.data.data)).catch(() => {}).finally(() => setLoad(false));
  }, []);

  if (loading) return <LoadingPage />;
  const s = data || {};
  const bannerStats = { total: s.totalTasks||0, active: s.inProgressTasks||0, overdue: s.overdueTasks||0, done: s.doneTasks||0 };

  return (
    <div style={P.wrap}>
      <WelcomeBanner user={user} stats={bannerStats} />
      <WhosOutWidget />
      <StatGrid items={[
        { label: 'All Tasks',   value: s.totalTasks,      icon: '📋', color: '#2563EB', bg: '#EFF6FF', onClick: () => navigate('/tasks') },
        { label: 'Overdue',     value: s.overdueTasks,    icon: '🚨', color: '#EF4444', bg: '#FEF2F2' },
        { label: 'In Progress', value: s.inProgressTasks, icon: '⚡', color: '#F59E0B', bg: '#FFFBEB' },
        { label: 'Done',        value: s.doneTasks,       icon: '✅', color: '#10B981', bg: '#ECFDF5' },
      ]} />
      <TaskBlock title="🚨 Needs Attention"  tasks={s.recentOverdueTasks || []}     emptyText="No overdue tasks — team is on track!" />
      <TaskBlock title="⚡ In Progress"      tasks={s.recentInProgressTasks || []}  emptyText="No tasks in progress right now" />
    </div>
  );
}

/* ── Employee Dashboard ──────────────────────────────────────────────── */
export function EmployeeDashboard() {
  const { user }           = useAuth();
  const navigate           = useNavigate();
  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(true);

  useEffect(() => {
    dashboardApi.employee().then(r => setData(r.data.data)).catch(() => {}).finally(() => setLoad(false));
  }, []);

  if (loading) return <LoadingPage />;
  const s = data || {};
  const total   = (s.myPendingTasks?.length || 0) + (s.myInProgressTasks?.length || 0) + (s.myOverdueTasks?.length || 0) + (s.myDoneTasks?.length || 0);
  const bannerStats = { total, active: s.myInProgressTasks?.length||0, overdue: s.myOverdueTasks?.length||0, done: s.myDoneTasks?.length||0 };

  return (
    <div style={P.wrap}>
      <WelcomeBanner user={user} stats={bannerStats} />

      {/* Quick action */}
      {s.myOverdueTasks?.length > 0 && (
        <div style={{
          background: '#FEF2F2', borderRadius: '14px', padding: '14px 16px',
          marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px',
          border: '1px solid #FECACA', cursor: 'pointer',
        }}
          onClick={() => navigate('/tasks')}
        >
          <span style={{ fontSize: '22px' }}>🚨</span>
          <div>
            <p style={{ fontWeight: 700, color: '#DC2626', fontSize: '14px' }}>
              {s.myOverdueTasks.length} overdue task{s.myOverdueTasks.length > 1 ? 's' : ''} need your attention
            </p>
            <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '2px' }}>Tap to view and update</p>
          </div>
          <span style={{ marginLeft: 'auto', color: '#EF4444', fontSize: '18px' }}>→</span>
        </div>
      )}

      <TaskBlock title="🔥 Overdue"       tasks={s.myOverdueTasks || []}     emptyText="No overdue tasks — you're on top of things!" />
      <TaskBlock title="⚡ In Progress"   tasks={s.myInProgressTasks || []}  emptyText="No tasks in progress" />
      <TaskBlock title="📬 Pending"       tasks={s.myPendingTasks || []}     emptyText="No pending tasks" />
    </div>
  );
}

const P = {
  wrap: { padding: '20px 16px', maxWidth: '640px', margin: '0 auto' },
};

/* ── Default export (router entry point) ─────────────────────────────── */
export default function Dashboards() {
  const { user } = useAuth();
  if (!user) return <LoadingPage />;
  if (user.role === 'SUPER_ADMIN') return <SuperAdminDashboard />;
  if (user.role === 'ADMIN')       return <AdminDashboard />;
  return <EmployeeDashboard />;
}
